import { Activity } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, ApiError, setCsrfToken } from '../api/client';
import { Alert, Field } from '../ui';

function AuthFrame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="auth-page">
      <div className="card auth-card">
        <div className="card-body">
          <div className="brand" style={{ padding: 0 }}>
            <span className="brand-mark"><Activity size={16} /></span>CFTV Central
          </div>
          <h1>{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

export function LoginPage({ onLoggedIn }: { onLoggedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ csrfToken: string }>('/api/v1/auth/login', { email, password });
      setCsrfToken(r.csrfToken);
      onLoggedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao entrar');
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthFrame title="Entrar">
      <form className="stack" onSubmit={submit}>
        <Field label="E-mail">
          <input className="input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        </Field>
        <Field label="Senha">
          <input className="input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        {error && <Alert kind="crit">{error}</Alert>}
        <button className="btn primary" type="submit" disabled={busy}>{busy ? <span className="spinner" /> : null}Entrar</button>
        <p className="faint" style={{ margin: 0, fontSize: 12.5 }}>
          Esqueceu a senha? Peça ao administrador da sua empresa um link de redefinição.
        </p>
      </form>
    </AuthFrame>
  );
}

const tokenFromHash = () => (typeof window !== 'undefined' ? window.location.hash.slice(1) : '');

export function AcceptInvitePage() {
  const nav = useNavigate();
  const [token] = useState(tokenFromHash);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) return setError('As senhas não conferem');
    try {
      await api.post('/api/v1/auth/invitations/accept', { token, name: name || undefined, password });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao aceitar o convite');
    }
  };
  if (!token) return <AuthFrame title="Convite"><Alert kind="crit">Link de convite incompleto.</Alert></AuthFrame>;
  return (
    <AuthFrame title="Aceitar convite">
      {done ? (
        <>
          <Alert kind="ok">Acesso liberado. Entre com seu e-mail e senha.</Alert>
          <button className="btn primary" onClick={() => nav('/')}>Ir para o login</button>
        </>
      ) : (
        <form className="stack" onSubmit={submit}>
          <p className="muted" style={{ margin: 0 }}>
            Se você já tem conta na plataforma, informe a <b>senha atual</b> para vincular a nova empresa. Caso contrário, defina uma senha (mínimo de 12 caracteres).
          </p>
          <Field label="Seu nome (novas contas)">
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Senha">
            <input className="input" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Field label="Confirmar senha">
            <input className="input" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </Field>
          {error && <Alert kind="crit">{error}</Alert>}
          <button className="btn primary" type="submit">Aceitar convite</button>
        </form>
      )}
    </AuthFrame>
  );
}

export function ResetPasswordPage() {
  const [token] = useState(tokenFromHash);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) return setError('As senhas não conferem');
    try {
      await api.post('/api/v1/auth/password-reset/complete', { token, password });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao redefinir a senha');
    }
  };
  return (
    <AuthFrame title="Redefinir senha">
      {done ? (
        <>
          <Alert kind="ok">Senha alterada. Todas as sessões anteriores foram encerradas.</Alert>
          <Link className="btn primary" to="/">Entrar</Link>
        </>
      ) : (
        <form className="stack" onSubmit={submit}>
          <Field label="Nova senha" hint="Mínimo de 12 caracteres.">
            <input className="input" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Field label="Confirmar nova senha">
            <input className="input" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </Field>
          {error && <Alert kind="crit">{error}</Alert>}
          <button className="btn primary" type="submit" disabled={!token}>Salvar nova senha</button>
        </form>
      )}
    </AuthFrame>
  );
}
